import type { InvestmentSummary } from "@eu.mdotm.api/sphere";
import { IconWalls } from "$root/components/IconWall";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { days } from "$root/utils";
import { defaultWidgetsGridConfig } from "$root/widgets-architecture/layout/WidgetsGrid/default-grids-config";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetLoading, useWidgetWalled } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import { asOfMissingDataWall } from "./as-of/missingDataWall";
import type { NodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import type { UseQueryResult } from "@tanstack/react-query";
import { type ReactNode, useContext } from "react";
import { match } from "ts-pattern";

const { PORTFOLIO_DETAILS, PORTFOLIO_ENHANCEMENT, PORTFOLIO_REFERENCE } = defaultWidgetsGridConfig;
const widgetKeys = [PORTFOLIO_DETAILS, PORTFOLIO_ENHANCEMENT, PORTFOLIO_REFERENCE].flatMap((config) =>
	config.widgets.map(({ i }) => i),
);

type PortfolioWidgetStatusIconWalls = {
	error?: ReactNode;
	calculating?: ReactNode;
	waitingForHistoricalData?: ReactNode;
	empty?: ReactNode; // for portfoliometrics (use Portfolio	Monitoring Empty Data variant)
};

type WidgetKeys = (typeof widgetKeys)[number];
type PortfolioWidgetStatusProps = {
	summary: InvestmentSummary;
	widgetKey: WidgetKeys;
	iconWalls?: PortfolioWidgetStatusIconWalls;
};

export enum WidgetStatus {
	ERROR = "error",
	CALCULATING = "calculating",
	WAIT_FOR_HISTORICAL_DATA = "waitingForHistoricalData",
	/**
	 * Nothing to show. Either the live summary says so — `portfolioWidgetMissingDataReason` answers this
	 * for a portfolio whose enhancement is executing — or a widget's own query mapper says so about the
	 * payload it read. Both take the widget's `empty` wall, and both take the dated wall in its place
	 * while a date is pinned (MDOT-24860).
	 */
	EMPTY = "empty",
	READY = "ready",
}

function missingHistoricalDataReason(summary: InvestmentSummary) {
	const interval = new Date(summary.lastReportUpdate ?? 0).getTime() - new Date(summary.creationDate ?? 0).getTime();
	if (interval < days(2)) {
		return WidgetStatus.WAIT_FOR_HISTORICAL_DATA;
	}
	return WidgetStatus.CALCULATING;
}

export type WidgetStatusUnion = `${WidgetStatus}`;
export type PortfolioWidgetData<TData> =
	| {
			widgetStatus: WidgetStatus.READY;
			data: NonNullable<TData>;
	  }
	| {
			widgetStatus: Exclude<WidgetStatusUnion, "ready">;
			data: TData;
	  };

/**
 * Walls a portfolio widget falls back to when it has no data to render. Shared by the two paths that
 * can get there — a widgetStatus other than "ready" and an outright failed fetch — so the same
 * condition never shows up in two different shapes.
 */
const defaultPortfolioWidgetIconWalls: Required<PortfolioWidgetStatusIconWalls> = {
	error: <IconWalls.ErrorPortfolioWidgetData />,
	calculating: <IconWalls.CalculatingPortfolioWidgetData />,
	waitingForHistoricalData: <IconWalls.WaitForHistoricalPortfolioWidgetData />,
	empty: <IconWalls.DataNotAvailable />,
};

/**
 * The wall a widget shows while the as-of snapshot it would read is not available yet — `null` once it
 * is readable, and while no date is pinned (MDOT-24860).
 *
 * Every portfolio-details widget reaches this gate: through the two bases below, or directly for the
 * commentary widget, which wraps its own query. It lives here rather than in each widget because a
 * snapshot is generated whole and announced by a single event, so there is no state in which one
 * widget could render and another could not. What the user is waiting for — and which date — is said
 * once, by the page's as-of banner, instead of thirteen times over.
 *
 * A widget's own `iconWalls` entry wins here, exactly as in the live view:
 *
 * - a snapshot mid-generation takes the calculating wall, and CALCULATING is a state the live view has
 *   too, so a widget that dresses it keeps its dressing;
 * - a snapshot that could not be fetched is a request failure like any other. Folding it into an
 *   explanation about the date would hide real 5xx behind a sentence that says nothing is wrong.
 */
export function useAsOfSnapshotWall(iconWalls?: PortfolioWidgetStatusIconWalls): React.ReactNode | null {
	const { asOfSnapshot } = useContext(PortfolioContext);
	const walls = { ...defaultPortfolioWidgetIconWalls, ...iconWalls };
	switch (asOfSnapshot?.kind) {
		case "generating":
			return walls.calculating;
		case "error":
			return walls.error;
	}
	return null;
}

/**
 * The wall that stands in for a widget's own empty state once the snapshot is readable and holds
 * nothing for it — `null` while no date is pinned, and for a status the pinned date does not change the
 * meaning of (MDOT-24860).
 *
 * It reads a widgetStatus, so it belongs to the base that has one: `PortfolioWidgetBase`, reached once
 * a widget's query has resolved. `PortfolioQueryWidgetBase` takes the snapshot gate above and nothing
 * else — every status it could inspect reaches this hook one level down.
 *
 * Two statuses collapse into the single dated wall. EMPTY and WAIT_FOR_HISTORICAL_DATA both promise the
 * metric is on its way — a promise about today, made on a date that is already over and will never gain
 * that metric. EMPTY says it whether it was the live summary or a widget's own mapper that answered, and
 * the promise is equally false either way. The other two are left alone:
 *
 * - CALCULATING is derived from the live summary actually recomputing, which is true whichever date
 *   the page is pinned to — the wall is not lying, so it stays.
 * - ERROR (and an outright failed fetch, handled in the query base) is still a request failure.
 *
 * For the two that collapse, a widget's own `iconWalls` entry deliberately does not win, and that is not
 * a loosely held preference: a widget's `waitingForHistoricalData` or `empty` entry is by construction a
 * restatement of the very claim this gate exists to stop making, so honouring it would leave the feature
 * switched off. Verified rather than assumed — `WAIT_FOR_HISTORICAL_DATA` is
 * reachable for exactly two widgets, `Attribution` and `ContributionVolatility`, and *both* override it
 * (with their horizon select above the stock wall), so the override contract applied here would have
 * consumed this branch in every case there is. What the override loses is that select, which steers
 * nothing on a date that has nothing to show.
 */
function useAsOfStatusWall(widgetStatus: WidgetStatusUnion | undefined): React.JSX.Element | null {
	const { asOf } = useContext(PortfolioContext);
	return asOfMissingDataWall(
		asOf,
		widgetStatus === WidgetStatus.EMPTY || widgetStatus === WidgetStatus.WAIT_FOR_HISTORICAL_DATA,
	);
}

export function PortfolioQueryWidgetBase<TData = unknown, TError = unknown>(props: {
	query: UseQueryResult<PortfolioWidgetData<TData>, TError>;
	// children: NodeOrFn<NonNullable<TData>>;
	children(queryResult: NonNullable<TData>, query: UseQueryResult<PortfolioWidgetData<TData>, TError>): React.ReactNode;
	errorFallback?: NodeOrFn<{ error?: TError; retry(): Promise<void> }>;
	iconWalls?: PortfolioWidgetStatusIconWalls;
}): React.JSX.Element {
	// Above the query wrapper, because a snapshot mid-generation is served as 404: the widget's query is
	// disabled until it is stored, and a disabled query sits at `isLoading` — the loading fallback below
	// would hold the page for as long as it is pinned.
	const asOfWall = useAsOfSnapshotWall(props.iconWalls);
	const { asOfSnapshot } = useContext(PortfolioContext);
	// A generating snapshot is a read the query cannot report: it stays disabled until the snapshot is
	// stored. The error wall is terminal, and a spinner over it would never end.
	useWidgetLoading(asOfWall ? asOfSnapshot?.kind === "generating" : props.query.isLoading);
	// Both walls this base decides replace the widget's content and unmount its publisher. A failed fetch
	// is one on the wrapper's own condition — an error with no data held — since the wrapper keeps the
	// children over a refetch that fails while data is shown.
	useWidgetWalled(asOfWall != null || (props.query.isError && props.query.data === undefined));
	if (asOfWall) {
		return <>{asOfWall}</>;
	}
	return (
		<ReactQueryWrapperBase
			query={props.query}
			// A failed fetch is the same thing to the user as a payload carrying WidgetStatus.ERROR, so
			// default it to the very wall the status path would have shown (the widget's own override, if
			// it has one). Left unset, ReactQueryWrapperBase falls back to its generic inline "Something
			// went wrong" + Retry, which the status path can never produce — one condition, two looks.
			errorFallback={props.errorFallback ?? props.iconWalls?.error ?? defaultPortfolioWidgetIconWalls.error}
			loadingFallback={<IconWalls.CalculatingApi />}
		>
			{(forward, rq) => {
				return (
					<PortfolioWidgetBase {...forward} iconWalls={props.iconWalls}>
						{(d) => props.children(d, rq)}
					</PortfolioWidgetBase>
				);
			}}
		</ReactQueryWrapperBase>
	);
}

export function PortfolioWidgetBase<TData = unknown>(
	props: PortfolioWidgetData<TData> & {
		children(queryResult: NonNullable<TData>): React.ReactNode;
		iconWalls?: PortfolioWidgetStatusIconWalls;
	},
): React.JSX.Element {
	const iconWalls: PortfolioWidgetStatusIconWalls = {
		...defaultPortfolioWidgetIconWalls,
		...props.iconWalls,
	};

	// The snapshot outranks the data's own status: one still being computed is served as 404, so whatever
	// `widgetStatus` says at that point was derived from a payload that never arrived. Once it is
	// readable, the status is what decides between the date-specific wall and the live ones.
	const snapshotWall = useAsOfSnapshotWall(props.iconWalls);
	const statusWall = useAsOfStatusWall(props.widgetStatus);
	const asOfWall = snapshotWall ?? statusWall;
	if (asOfWall) {
		return <>{asOfWall}</>;
	}

	return <>{props.widgetStatus === "ready" ? props.children(props.data) : iconWalls[props.widgetStatus]}</>;
}

type PortfolioWidgetMissingDataReasonReturn = Exclude<WidgetStatusUnion, "ready">;
export function portfolioWidgetMissingDataReason(
	summary: InvestmentSummary,
	widgetName: WidgetKeys,
): PortfolioWidgetMissingDataReasonReturn {
	switch (widgetName) {
		case "PortfolioPerformance":
		case "PerformanceMetricsBlock":
			return match(summary)
				.returnType<PortfolioWidgetMissingDataReasonReturn>()
				.with({ status: "ERROR" }, () => WidgetStatus.ERROR)
				.with({ status: "CALCULATING", action: "EDIT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "UPLOAD" }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "CREATION" }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "ENHANCEMENT", hasProposalDraft: false }, missingHistoricalDataReason)
				.with({ status: "CALCULATING", action: "ENHANCEMENT", hasProposalDraft: true }, missingHistoricalDataReason)
				.with({ status: "ACCEPTED", action: "ENHANCEMENT" }, missingHistoricalDataReason)
				.with({ status: "ACCEPTED", action: "EDIT" }, missingHistoricalDataReason)
				.with({ status: "ACCEPTED", action: "UPLOAD" }, missingHistoricalDataReason)
				.with({ status: "READY", action: "CREATION" }, missingHistoricalDataReason)
				.with({ status: "READY", action: "EDIT" }, missingHistoricalDataReason)
				.with({ status: "READY", action: "UPLOAD" }, missingHistoricalDataReason)
				.with({ status: "PROPOSAL_READY", action: "ENHANCEMENT" }, missingHistoricalDataReason)
				.with({ status: "EXECUTING", action: "ENHANCEMENT" }, missingHistoricalDataReason)
				.otherwise(() => WidgetStatus.ERROR);
		case "ContributionVolatility":
		case "Attribution":
			return match(summary)
				.returnType<PortfolioWidgetMissingDataReasonReturn>()
				.with({ status: "ERROR" }, () => WidgetStatus.ERROR)
				.with({ status: "CALCULATING", action: "EDIT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "UPLOAD" }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "CREATION" }, () => WidgetStatus.CALCULATING)
				.with(
					{ status: "CALCULATING", action: "ENHANCEMENT", hasProposalDraft: false },
					() => WidgetStatus.WAIT_FOR_HISTORICAL_DATA,
				)
				.with(
					{ status: "CALCULATING", action: "ENHANCEMENT", hasProposalDraft: true },
					() => WidgetStatus.WAIT_FOR_HISTORICAL_DATA,
				)
				.with({ status: "ACCEPTED", action: "ENHANCEMENT" }, () => WidgetStatus.WAIT_FOR_HISTORICAL_DATA)
				.with({ status: "ACCEPTED", action: "EDIT" }, () => WidgetStatus.WAIT_FOR_HISTORICAL_DATA)
				.with({ status: "ACCEPTED", action: "UPLOAD" }, () => WidgetStatus.WAIT_FOR_HISTORICAL_DATA)
				.with({ status: "READY", action: "CREATION" }, () => WidgetStatus.WAIT_FOR_HISTORICAL_DATA)
				.with({ status: "READY", action: "EDIT" }, () => WidgetStatus.WAIT_FOR_HISTORICAL_DATA)
				.with({ status: "READY", action: "UPLOAD" }, () => WidgetStatus.WAIT_FOR_HISTORICAL_DATA)
				.with({ status: "PROPOSAL_READY", action: "ENHANCEMENT" }, () => WidgetStatus.WAIT_FOR_HISTORICAL_DATA)
				.with({ status: "EXECUTING", action: "ENHANCEMENT" }, () => WidgetStatus.WAIT_FOR_HISTORICAL_DATA)
				.otherwise(() => WidgetStatus.ERROR);
		case "PortfolioMonitoringBlock":
		case "PortfolioCommentaryMDBlock":
		case "ExanteMetricsBlock":
		case "ExanteContributionVolatility":
		case "PortfolioFactorsExposure":
		case "ExposureEvolve":
		case "ExposureCompare":
		case "PortfolioHistoryBlock":
			return match(summary)
				.returnType<PortfolioWidgetMissingDataReasonReturn>()
				.with({ status: "ERROR" }, () => WidgetStatus.ERROR)
				.with({ status: "CALCULATING", action: "EDIT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "UPLOAD" }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "CREATION" }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "ENHANCEMENT", hasProposalDraft: false }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "ENHANCEMENT", hasProposalDraft: true }, () => WidgetStatus.CALCULATING)
				.with({ status: "ACCEPTED", action: "ENHANCEMENT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "ACCEPTED", action: "EDIT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "ACCEPTED", action: "UPLOAD" }, () => WidgetStatus.CALCULATING)
				.with({ status: "READY", action: "CREATION" }, () => WidgetStatus.CALCULATING)
				.with({ status: "READY", action: "EDIT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "READY", action: "UPLOAD" }, () => WidgetStatus.CALCULATING)
				.with({ status: "PROPOSAL_READY", action: "ENHANCEMENT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "EXECUTING", action: "ENHANCEMENT" }, () => WidgetStatus.EMPTY)
				.otherwise(() => WidgetStatus.ERROR);
		case "Composition":
			return match(summary)
				.returnType<PortfolioWidgetMissingDataReasonReturn>()
				.with({ status: "ERROR" }, () => WidgetStatus.ERROR)
				.with({ status: "CALCULATING", action: "EDIT" }, () => WidgetStatus.CALCULATING) // be: req change status behaviour
				.with({ status: "CALCULATING", action: "UPLOAD" }, () => WidgetStatus.CALCULATING) // be: req change status behaviour
				.with({ status: "CALCULATING", action: "CREATION" }, () => WidgetStatus.CALCULATING) // be: req change status behaviour
				.with({ status: "CALCULATING", action: "ENHANCEMENT", hasProposalDraft: false }, () => WidgetStatus.CALCULATING)
				.with({ status: "CALCULATING", action: "ENHANCEMENT", hasProposalDraft: true }, () => WidgetStatus.CALCULATING)
				.with({ status: "ACCEPTED", action: "ENHANCEMENT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "ACCEPTED", action: "EDIT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "ACCEPTED", action: "UPLOAD" }, () => WidgetStatus.CALCULATING)
				.with({ status: "READY", action: "CREATION" }, () => WidgetStatus.CALCULATING)
				.with({ status: "READY", action: "EDIT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "READY", action: "UPLOAD" }, () => WidgetStatus.CALCULATING)
				.with({ status: "PROPOSAL_READY", action: "ENHANCEMENT" }, () => WidgetStatus.CALCULATING)
				.with({ status: "EXECUTING", action: "ENHANCEMENT" }, () => WidgetStatus.EMPTY)
				.otherwise(() => WidgetStatus.ERROR);
		default:
			return "error";
	}
}

export function PortfolioWidgetStatus(props: PortfolioWidgetStatusProps): ReactNode {
	const iconWalls: PortfolioWidgetStatusIconWalls = {
		error: <IconWalls.ErrorData />,
		calculating: <IconWalls.CalculatingData />,
		waitingForHistoricalData: <IconWalls.HistoricalDataNotAvailable />,
		empty: <IconWalls.DataNotAvailable />,
		...props.iconWalls,
	};
	return iconWalls[portfolioWidgetMissingDataReason(props.summary, props.widgetKey)];
}
